import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { helper } from 'src/environments/helper';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { DialogService } from 'src/app/layout/services/dialog.service';
import { environment } from 'src/environments/environment';
import { OperatoriService } from './operatori.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import Swal from 'sweetalert2';
import { EditOperatorComponent } from './edit-operator/edit-operator.component';
import { UpdateFunctieOperatorComponent } from './update-functie-operator/update-functie-operator.component'
import { FormControl, FormGroup } from '@angular/forms';
import { CommonService } from 'src/app/services/common.service';
import { Generic, GenericUser } from 'src/app/interfaces/generic.interface';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
    selector: 'app-operatori',
    templateUrl: './operatori.component.html',
    styleUrls: ['./operatori.component.scss']
})
export class OperatoriComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    functiiMap: { [key: number]: string } = {};
    names!: GenericUser[];
    functii!: Generic[];

    directiiActive: Generic[] = [];
    compartimenteActive!: Generic[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii!: any[];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredFunctii: any[] = [];
    functiiControl = new FormControl();

    dtLink =
        environment.interop.basePath +
        environment.interop.dgitl.nomenclatoare.operatori.getOperatoriDT;

    //Form Initial Values

    searchParamsForm!: FormGroup;

    initialFormValues = {
        name: '',
        functie: -1,
        directie: -1,
        compartiment: -1,
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            name: new FormControl(''),
            functie: new FormControl(-1),
            directie: new FormControl(-1),
            compartiment: new FormControl(-1),
        });
    }

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private commonService: CommonService,
        public permissionsService: PermissionsService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.getFunctii();
        this.buildSearchParamsForm();
        this.loadDirectiiActive();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.rerenderDT();
        this.showCard = true;
        this.onDirectieChange(-1);

        this.filteredDirectii = this.directiiActive;
        this.directiiControl.setValue(null);

        this.compartimenteControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();
    }

    applyFilters(): void {
        this.showCard = false;
        this.rerenderDT(true);
    }

    getFunctii() {
        this.commonService.getMappedFunctii().subscribe((data) => {
            this.functii = [...data];
            this.filteredFunctii = [...data];
            data.forEach((item) => {
                this.functiiMap[item.id] = item.name;
            });
        });
    }

    filterFunctii(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.functii.filter(functie =>
            this.normalizeString(functie.name).includes(normalizedSearchTerm)
        ));
    }

    onFunctieSelectClosed() {
        const selectedValue = this.functiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredFunctii.some(functie =>
                functie.id === this.searchParamsForm.get('functie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.functiiControl.setValue(null);
                this.filteredFunctii = this.functii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            responsive: true,
            searching: false,
            serverSide: true,
            pageLength: 25,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.filters = this.searchParamsForm.value;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((res: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: res.data.recordsTotal,
                            recordsFiltered: res.data.recordsFiltered,
                            data: res.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id_user',
                },
                {
                    title: 'Nume',
                    data: 'name',
                },
                {
                    title: 'Funcție',
                    data: 'functie_id',
                    render: (data: any) => this.functiiMap[data] || 'N/A',
                },
                {
                    title: 'Instituție',
                    data: 'denumire_sesizare_departament',
                    orderable: false,
                    render: function (data: any) {
                        return data === null || data === undefined ? 'N/A' : data;
                    },
                },
                {
                    title: 'Compartiment',
                    data: 'denumire_sesizare_compartiment',
                    orderable: false,
                    render: function (data: any) {
                        return data === null || data === undefined ? 'N/A' : data;
                    },
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    className: 'all',
                    orderable: false,
                    render: (row: any) => {
                        return `
							<button type="button" class="action-btn edit" title="Editare">
							<img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
							</button> 
							&nbsp;
							<button class="action-btn signatures">
                				<img class="btn-img" src="/assets/icons/simple-signature-contract.svg" alt="signatures"/>
              				</button>

						`;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;

                const signaturesIcon = lastTd?.querySelector(
                    '.signatures'
                ) as HTMLElement;


                if (editIcon) {
                    if (!this.permissionsService.hasPermission('dgitl-module.settings.operatori.update')) {
                        editIcon.style.display = 'none';
                    }

                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (signaturesIcon) {
                    if (!this.permissionsService.hasPermission('dgitl-module.settings.operatori.update-functie')) {
                        signaturesIcon.style.display = 'none';
                    }
                    signaturesIcon.addEventListener('click', () => {
                        this.openUpdateFunctieOperator(data);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.isLoading = true;
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            this.isLoading = false;
            dtInstance.draw(resetPaging);
        });
    }

    openEditDialog(data: any) {
        const dialogRef = this.dialog.open(EditOperatorComponent, {
            minHeight: '80vh',
            maxHeight: '80vh',
            minWidth: '50vw',
            maxWidth: '80vw',
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Actualizare cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openUpdateFunctieOperator(data: any) {
        const dialogRef = this.dialog.open(UpdateFunctieOperatorComponent, {
            maxHeight: '40vh',
            minWidth: '40vw',
            maxWidth: '40vw',
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            this.rerenderDT();
            if (success) {
                this.isLoading = true;
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Actualizare cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.functiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredFunctii = this.functii;
                    return of(this.functii);
                }
                return this.filterFunctii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredFunctii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(categorie =>
            this.normalizeString(categorie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadDirectiiActive() {
        this.isFilterLoading = true;
        this.commonService.getDirectiiActive().pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.directiiActive = results;
                this.filteredDirectii = results;
                return;
            },
            error: (err) => {
                console.error(err);
                this.filteredDirectii = [];
            }
        })
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(categorie =>
                categorie.id === this.searchParamsForm.get('directie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.searchParamsForm.controls['compartiment'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isFilterLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimenteActive = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            }
        });
    }

    onCompartimenteSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.searchParamsForm.get('compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterCompartimente(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        ));
    }
}
